import { Box, Button, HStack, Icon } from "@chakra-ui/react";
import type { DefaultTFuncReturn } from "i18next";
import { useTranslation } from "react-i18next";
import { FiCheck, FiX } from "react-icons/fi";

interface Props {
  value: boolean | null;
  // Optional: a locked/read-only control never invokes it.
  onChange?: (value: boolean | null) => void;
  isReadOnly?: boolean;
  isLocked?: boolean;
  isPositiveDisabled?: boolean;
  isNegativeDisabled?: boolean;
  isClearable?: boolean;
  positiveLabel?: DefaultTFuncReturn;
  negativeLabel?: DefaultTFuncReturn;
}

export const QualificationVerdictControl = ({
  value,
  onChange,
  isReadOnly,
  isLocked,
  isPositiveDisabled,
  isNegativeDisabled,
  isClearable = true,
  positiveLabel,
  negativeLabel,
}: Props) => {
  const { t } = useTranslation();
  const readOnly = isReadOnly || isLocked;

  const select = (next: boolean) => {
    if (readOnly) return;
    if (next && isPositiveDisabled) return;
    if (!next && isNegativeDisabled) return;
    if (value === next && !isClearable) return;
    onChange?.(value === next ? null : next);
  };

  return (
    <HStack
      spacing="1"
      bg="smBackgroundSecondary"
      borderRadius="md"
      p="1"
      align="center"
      cursor={isLocked ? "not-allowed" : undefined}
    >
      <VerdictButton
        active={value === true}
        activeColor="smSuccess"
        icon={FiCheck}
        label={positiveLabel ?? t("Fulfilled")}
        onClick={() => select(true)}
        isReadOnly={readOnly || isPositiveDisabled}
        greyed={isLocked}
      />
      <VerdictButton
        active={value === false}
        activeColor="smError"
        icon={FiX}
        label={negativeLabel ?? t("Not fulfilled")}
        onClick={() => select(false)}
        isReadOnly={readOnly || isNegativeDisabled}
        greyed={isLocked}
      />
    </HStack>
  );
};

interface VerdictButtonProps {
  active: boolean;
  activeColor: string;
  icon: typeof FiCheck;
  label: string;
  onClick: () => void;
  isReadOnly?: boolean;
  greyed?: boolean;
}

const VerdictButton = ({ active, activeColor, icon, label, onClick, isReadOnly, greyed }: VerdictButtonProps) => (
  <Button
    size="sm"
    variant="ghost"
    onClick={onClick}
    isDisabled={isReadOnly}
    aria-pressed={active}
    bg={active ? "smBackground" : "transparent"}
    boxShadow={active ? "sm" : "none"}
    color={active ? activeColor : greyed ? "smTextDisabled" : "smMuted"}
    fontWeight="medium"
    _hover={{
      bg: active ? "smBackground" : "smBackgroundSecondary",
      color: active ? activeColor : greyed ? "smTextDisabled" : "smText",
    }}
    _disabled={greyed ? { opacity: 1, cursor: "not-allowed" } : undefined}
    px="3"
    h="8"
  >
    <Icon as={icon} mr="1.5" />
    <Box as="span">{label}</Box>
  </Button>
);
